/**
 * Archiv: Umfang, Importe und das Einliefern neuer Exporte.
 *
 * Der Upload ist der Rückfallweg. Der übliche Weg ist der Kurzbefehl auf dem
 * Telefon, der die Datei unmittelbar an Encom schickt.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Upload } from "lucide-react";

import { api, ApiFehler, type IngestBilanz, type Importzeile, type Statistik } from "@/api.ts";
import { datumLokal, jahrLokal, zeitpunktLokal } from "@/zeit.ts";
import { ICloudAnmeldung } from "@/komponenten/ICloudAnmeldung.tsx";

const ARTEN: Record<string, string> = {
  "in passenger vehicle": "Auto",
  "in vehicle": "Fahrzeug",
  walking: "zu Fuß",
  cycling: "Fahrrad",
  "in train": "Zug",
  "in bus": "Bus",
  "in subway": "U-Bahn",
  "in tram": "Straßenbahn",
  "in ferry": "Fähre",
  flying: "Flug",
  sailing: "Segeln",
  motorcycling: "Motorrad",
  running: "Laufen",
  skiing: "Ski",
  unknown: "unbestimmt",
};

const zahl = (n: number) => n.toLocaleString("de-DE");

export function ArchivSeite() {
  const [statistik, setStatistik] = useState<Statistik | null>(null);
  const [importe, setImporte] = useState<Importzeile[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [bilanz, setBilanz] = useState<IngestBilanz | null>(null);
  const dateiwahl = useRef<HTMLInputElement>(null);

  const laden = useCallback(() => {
    Promise.all([api.statistik(), api.importe()])
      .then(([s, i]) => {
        setStatistik(s);
        setImporte(i);
      })
      .catch((e: unknown) => setFehler(e instanceof ApiFehler ? e.message : String(e)));
  }, []);

  useEffect(laden, [laden]);

  const einliefern = async (datei: File) => {
    setLaeuft(true);
    setFehler(null);
    setBilanz(null);
    try {
      setBilanz(await api.einliefern(datei));
      laden();
    } catch (e: unknown) {
      setFehler(e instanceof ApiFehler ? e.message : String(e));
    } finally {
      setLaeuft(false);
      if (dateiwahl.current) dateiwahl.current.value = "";
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4 pl-16 lg:pl-4">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* ------------------------------------------------------- Einliefern */}
        <section className="rounded-lg border border-border bg-surface p-5">
          <h2 className="text-lg font-semibold">Export einliefern</h2>
          <p className="mt-1 text-sm text-text-muted">
            Google Maps → Profilbild → <em>Einstellungen</em> → <em>Standort &amp; Datenschutz</em>{" "}
            → <strong>Zeitachsendaten exportieren</strong>. Die Datei heißt unter iOS{" "}
            <code className="rounded bg-page-bg px-1">location-history.json</code>, unter Android{" "}
            <code className="rounded bg-page-bg px-1">Timeline.json</code>.
          </p>
          <p className="mt-2 text-sm text-text-muted">
            Dieselbe Datei zweimal einzuliefern schadet nicht — bereits bekannte Segmente werden
            erkannt und nicht doppelt abgelegt.
          </p>

          <div className="mt-4 flex items-center gap-3">
            <input
              ref={dateiwahl}
              type="file"
              accept="application/json,.json"
              onChange={(e) => {
                const datei = e.target.files?.[0];
                if (datei) void einliefern(datei);
              }}
              className="hidden"
            />
            <button
              onClick={() => dateiwahl.current?.click()}
              disabled={laeuft}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm text-white hover:bg-primary-dark disabled:opacity-50"
            >
              <Upload size={16} />
              {laeuft ? "wird eingelesen…" : "Datei auswählen"}
            </button>
          </div>

          {bilanz && (
            <div className="mt-4 rounded-lg border border-border bg-page-bg p-4 text-sm">
              {bilanz.schonBekannt ? (
                <p className="flex items-start gap-2">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning" />
                  {bilanz.warnungen[0]}
                </p>
              ) : (
                <>
                  <p className="flex items-center gap-2 font-medium">
                    <CheckCircle2 size={16} className="text-success" />
                    Eingelesen ({bilanz.format})
                  </p>
                  <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-4">
                    <Kennzahl name="gelesen" wert={bilanz.gesehen} />
                    <Kennzahl name="neu" wert={bilanz.neu} />
                    <Kennzahl name="revidiert" wert={bilanz.revidiert} />
                    <Kennzahl name="unverändert" wert={bilanz.unveraendert} />
                  </dl>
                  {bilanz.von && (
                    <p className="mt-2 text-xs text-text-muted">
                      Zeitraum {datumLokal(bilanz.von)} bis {datumLokal(bilanz.bis)}
                    </p>
                  )}
                  {bilanz.warnungen.map((w) => (
                    <p key={w} className="mt-1 text-xs text-text-muted">
                      {w}
                    </p>
                  ))}
                </>
              )}
            </div>
          )}

          {fehler && (
            <p className="mt-4 flex items-start gap-2 text-sm text-danger">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              {fehler}
            </p>
          )}
        </section>

        <ICloudAnmeldung />

        {/* --------------------------------------------------------- Umfang */}
        {statistik && (
          <section className="rounded-lg border border-border bg-surface p-5">
            <h2 className="text-lg font-semibold">Umfang</h2>
            <dl className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <Kachel
                name="Zeitraum"
                wert={`${jahrLokal(statistik.von)}–${jahrLokal(statistik.bis)}`}
              />
              <Kachel name="Aufenthalte" wert={zahl(statistik.besuche)} />
              <Kachel name="Wege" wert={zahl(statistik.wege)} />
              <Kachel name="Pfadpunkte" wert={zahl(statistik.punkte)} />
              <Kachel name="Rohspuren" wert={zahl(statistik.spuren)} />
              <Kachel name="Reisen" wert={zahl(statistik.reisen)} />
              <Kachel
                name="Orte benannt"
                wert={`${zahl(statistik.orteAufgeloest)} / ${zahl(statistik.orte)}`}
                hinweis={
                  statistik.orteOhneKoordinaten > 0
                    ? `Von den ${zahl(statistik.orte - statistik.orteAufgeloest)} noch namenlosen ` +
                      `liefert Google ${zahl(statistik.orteOhneKoordinaten)} ohne Koordinaten — ` +
                      `ohne Punkt auf der Karte ist nichts aufzulösen.`
                    : undefined
                }
              />
              <Kachel
                name="Abgelöste Fassungen"
                wert={zahl(statistik.abgeloest)}
                hinweis="Segmente, die Google nachträglich geändert hat. Die alte Fassung bleibt erhalten."
              />
            </dl>

            {statistik.jahre.length > 0 && (
              <>
                <h3 className="mt-6 text-sm font-medium text-text-muted">Segmente je Jahr</h3>
                <div className="mt-2 flex items-end gap-1" style={{ height: 96 }}>
                  {statistik.jahre.map((j) => {
                    const groesstes = Math.max(...statistik.jahre.map((x) => x.anzahl));
                    return (
                      <div key={j.jahr} className="flex flex-1 flex-col items-center gap-1">
                        <div
                          className="w-full rounded-t bg-primary-light"
                          style={{ height: `${Math.max(2, (j.anzahl / groesstes) * 80)}px` }}
                          title={`${j.jahr}: ${zahl(j.anzahl)} Segmente`}
                        />
                        <span className="text-[10px] text-text-muted">{j.jahr.slice(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {statistik.aktivitaeten.length > 0 && (
              <>
                <h3 className="mt-6 text-sm font-medium text-text-muted">Fortbewegung</h3>
                <ul className="mt-2 space-y-1 text-sm">
                  {statistik.aktivitaeten.map((a) => (
                    <li key={a.art} className="flex items-baseline gap-3">
                      <span className="w-28 shrink-0">{ARTEN[a.art] ?? a.art}</span>
                      <span className="w-16 shrink-0 text-right text-text-muted">
                        {zahl(a.anzahl)} ×
                      </span>
                      <span className="w-24 shrink-0 text-right tabular-nums">
                        {zahl(Math.round(a.meter / 1000))} km
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        )}

        {/* -------------------------------------------------------- Importe */}
        {importe.length > 0 && (
          <section className="rounded-lg border border-border bg-surface p-5">
            <h2 className="text-lg font-semibold">Eingelesene Exporte</h2>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-text-muted">
                  <tr className="border-b border-border">
                    <th className="pb-2 font-medium">Eingelesen</th>
                    <th className="pb-2 font-medium">Datei</th>
                    <th className="pb-2 text-right font-medium">Gelesen</th>
                    <th className="pb-2 text-right font-medium">Neu</th>
                    <th className="pb-2 text-right font-medium">Revidiert</th>
                    <th className="pb-2 pl-6 font-medium">Zeitraum</th>
                  </tr>
                </thead>
                <tbody>
                  {importe.map((i) => (
                    <tr key={i.id} className="border-b border-border last:border-0">
                      <td className="py-2 whitespace-nowrap">
                        {zeitpunktLokal(i.importiertAm)}
                      </td>
                      <td className="max-w-40 truncate py-2" title={i.dateiname ?? ""}>
                        {i.dateiname ?? "—"}
                      </td>
                      <td className="py-2 text-right tabular-nums">{zahl(i.gesehen)}</td>
                      <td className="py-2 text-right tabular-nums">{zahl(i.neu)}</td>
                      <td className="py-2 text-right tabular-nums">{zahl(i.revidiert)}</td>
                      <td className="py-2 pl-6 whitespace-nowrap text-text-muted">
                        {datumLokal(i.von)} – {datumLokal(i.bis)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Kachel({ name, wert, hinweis }: { name: string; wert: string; hinweis?: string }) {
  return (
    <div title={hinweis}>
      <dt className="text-xs text-text-muted">{name}</dt>
      <dd className="mt-0.5 text-xl font-semibold tabular-nums">{wert}</dd>
    </div>
  );
}

function Kennzahl({ name, wert }: { name: string; wert: number }) {
  return (
    <div>
      <dt className="text-xs text-text-muted">{name}</dt>
      <dd className="tabular-nums">{zahl(wert)}</dd>
    </div>
  );
}
